"use client";

import * as RadixPopover from "@radix-ui/react-popover";
import { ReactNode } from "react";

interface ContainerProps {
  children: ReactNode;
  align?: "start" | "center" | "end";
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
  className?: string;
  /** Defaults to document.body. Point it at a modal Dialog's content element
   *  when the trigger is inside one, or the popover won't scroll. */
  container?: HTMLElement | null;
}

// Height is the smaller of the 30rem design cap and the space Radix measured to
// the screen edge, so the popover can never run off-screen where you can't
// reach its scrollbar. 100vh is only a fallback for before Radix has measured.
const Container = ({
  children,
  align = "start",
  side = "bottom",
  sideOffset = 8,
  className,
  container,
}: ContainerProps) => {
  return (
    <RadixPopover.Portal container={container}>
      <RadixPopover.Content
        align={align}
        side={side}
        sideOffset={sideOffset}
        className={`flex flex-col bg-white rounded-lg shadow-xs min-w-64 max-h-[min(30rem,var(--radix-popover-content-available-height,100vh))] z-50 border-light border outline-none data-[state=open]:data-[side=bottom]:animate-slideUpAndFade data-[state=open]:data-[side=left]:animate-slideRightAndFade data-[state=open]:data-[side=right]:animate-slideLeftAndFade data-[state=open]:data-[side=top]:animate-slideDownAndFade${className ? ` ${className}` : ""}`}
      >
        {children}
      </RadixPopover.Content>
    </RadixPopover.Portal>
  );
};

interface HeaderProps {
  children: ReactNode;
}

const Header = ({ children }: HeaderProps) => {
  return (
    <header className="flex flex-shrink-0 items-center justify-between border-b border-light py-2 px-3">
      {children}
    </header>
  );
};

interface ContentProps {
  children: ReactNode;
}

const Content = ({ children }: ContentProps) => {
  return (
    <div className="flex flex-1 flex-col min-h-0 py-2 px-3 overflow-y-auto">
      {children}
    </div>
  );
};

interface FooterProps {
  children: ReactNode;
}

const Footer = ({ children }: FooterProps) => {
  return (
    <footer className="flex flex-shrink-0 border-t border-light py-2 px-3">
      {children}
    </footer>
  );
};

const Root = RadixPopover.Root;
const Close = RadixPopover.Close;
const Trigger = RadixPopover.Trigger;
const Anchor = RadixPopover.Anchor;

export const Popover = {
  Container,
  Header,
  Content,
  Footer,
  Root,
  Close,
  Trigger,
  Anchor,
};
